import * as React from "react";
import { $, merge } from "glamor";
import { ProductTile } from "components/product-collection/product-collection-views/product-collection-tile-view/product-tile";
import { SmallTile } from "components/product-collection/product-collection-views/product-collection-grid-view/tool-panels/shop-tool-panel/small-tile";
import { Grid } from "react-virtualized/dist/commonjs/Grid";
import { AutoSizer } from "react-virtualized/dist/commonjs/AutoSizer";
import { InfiniteLoader } from "react-virtualized/dist/commonjs/InfiniteLoader";
import { List, Map, fromJS } from "immutable";
import uuid from "uuid/v4";
import { LINESHEET_PAGE_SIZE, Item as ProductDisplayFieldPaths } from "constants/product-collection";
import { LoadingShimmer } from "./loading-shimmer";
import { getCollectionSwatches, getVariantColorIds, IDisplayFields, ISchemaKey } from "helpers/product-collection";
import { formatMessage } from "lib/globalize-util";
import {
    getProductHeroImage,
    getProductImages,
} from "../product-collection-views/product-collection-detail-view/helpers";
import { getProductShopViewFields, getProductShopViewFieldsFallback, IShopViewFields } from "helpers/collection";
import { IShopViewSettings } from "../manage-shop-view/types";

const tombstoneRows = (grid, { columns = 0, ProductCard, fetchingRows = 0, resultLength = 0 }) => {
    if (!fetchingRows) {
        return;
    }

    // fill out remaining row
    while (grid[grid.length - 1] && grid[grid.length - 1].length < columns) {
        grid[grid.length - 1].push({
            width: ProductCard.width,
            height: ProductCard.height,
            index: resultLength,
        });
        resultLength += 1;
    }

    for (let i = 0; i < fetchingRows; i++) {
        grid.push([]);
        for (let j = 0; j < columns; j++) {
            grid[grid.length - 1].push({
                width: ProductCard.width,
                height: ProductCard.height,
                index: resultLength,
            });
            resultLength += 1;
        }
    }
};

const getGridForItems = ({ columns, globalProducts, ProductCard, fetchingRows }) => {
    const grid = [];
    let forceNewLine = false;
    let height = 0;
    for (let i = 0; i < globalProducts.size; i++) {
        const item = globalProducts.get(i);

        const spanAllColumns = false;

        let lastRow = grid.length - 1;

        if (grid.length === 0 || forceNewLine || grid[grid.length - 1].length >= columns) {
            grid.push([]);
            lastRow = grid.length - 1;
            forceNewLine = spanAllColumns;
            height += ProductCard.height + ProductCard.verticalOffset;
        }

        grid[lastRow].push({
            item,
            width: ProductCard.width,
            height: ProductCard.height,
            index: i,
        });
    }

    tombstoneRows(grid, {
        columns,
        fetchingRows,
        ProductCard,
        resultLength: globalProducts.size,
    });

    return { grid, height };
};

interface IProps {
    merchantId: string;
    brandEntities: any;
    collectionEditable: boolean;
    globalProducts: any;
    globalProductsIsFetching: any;
    globalProductNextPageQuery: any;
    onFetchNextGlobalProducts: any;
    inAssortmentProducts: { [key: string]: string[] };
    itemsId: string;
    items: List<any>;
    location: any;
    onAddItem: (itemsId, attributes?, index?) => void;
    onFetchGlobalProducts: (params: any) => void;
    scope: any;
    sidePanelWidth: number;
    overrideStyles?: any;
    router: any;
    params: any;
    shopViewFields: IDisplayFields;
    isSidePanel?: boolean;
    allowDuplicateProduct?: boolean;
    itemDisplayProperty: List<any>;
    shopViewSettings?: IShopViewSettings;
    rawItemDisplayProperty?: ISchemaKey[];
}

export class ShopSearch extends React.PureComponent<IProps, any> {
    _onRowsRendered: any;
    _grid: any;

    private muiStyles = {
        circularProgress: {
            width: "40px",
            margin: "100px auto",
        },
        imageSeparator: {
            paddingLeft: "8px",
            paddingRight: "8px",
        },
        datepickerIcon: {
            position: "relative",
            left: "-10px",
            top: "2px",
        },
        noResults: {
            display: "block",
            padding: "8px 0 0",
            position: "relative",
            height: "64px",
            lineHeight: "56px",
            textAlign: "center",
            fontWeight: "bold",
            fontSize: "18px",
        },
    };

    constructor(props) {
        super(props);

        this.state = {
            grid: [],
            height: 0,
            filters: this.setFiltersFromQueryParams(),
            filterOptions: [],
            allowDuplicateProduct: true,
        };
    }

    computeGrid = (props, ProductCard) => {
        const {
            globalProducts,
            globalProductsIsFetching,
            overrideStyles = { gridWidth: undefined, viewHorizontalOffset: undefined, viewWidth: undefined },
        } = props;

        const { gridWidth, viewHorizontalOffset, viewWidth } = overrideStyles;
        const newGridWidth = gridWidth !== undefined ? gridWidth : window.innerWidth - 167;

        const newViewWidth = viewWidth !== undefined ? viewWidth : ProductCard.width;
        const newViewHorizontalOffset =
            viewHorizontalOffset !== undefined ? viewHorizontalOffset : ProductCard.horizontalOffset;
        const columns = Math.min(
            Math.max(Math.floor(newGridWidth / (newViewWidth + newViewHorizontalOffset * 6)), 1),
            6
        );
        let grid = this.state.grid;
        let height = 0;

        const pageSize = LINESHEET_PAGE_SIZE;
        const fetchingRows = globalProductsIsFetching ? Math.floor(pageSize / columns) : 0;

        const result = getGridForItems({
            columns,
            globalProducts,
            ProductCard,
            fetchingRows,
        });
        height = result.height;
        grid = result.grid;

        this.setState({
            columns,
            grid,
            height,
            estimatedRows: grid.length,
        });
    };

    UNSAFE_componentWillMount() {
        const ProductCard = this.props.isSidePanel ? SmallTile : ProductTile;
        this.computeGrid(this.props, ProductCard);

        this.setState({
            ProductCard,
        });

        this.handleFetchProducts(this.state.filters);

        window.addEventListener("resize", this.handleResize, false);
    }

    componentWillUnmount() {
        window.removeEventListener("resize", this.handleResize, false);
    }

    handleResize = (event) => {
        this.computeGrid(this.props, this.state.ProductCard);
    };

    UNSAFE_componentWillReceiveProps(nextProps) {
        if (
            this.props.globalProductsIsFetching !== nextProps.globalProductsIsFetching ||
            this.props.globalProducts !== nextProps.globalProducts
        ) {
            this.computeGrid(nextProps, this.state.ProductCard);
        }
    }

    componentDidUpdate(prevProps, prevState) {
        if (
            this._grid &&
            (prevProps.globalProducts !== this.props.globalProducts ||
                prevProps.inAssortmentProducts !== this.props.inAssortmentProducts)
        ) {
            this._grid.recomputeGridSize(); // row heights could have changed depending on label and item order
        }

        if (prevProps.location.query.filters !== this.props.location.query.filters) {
            const newFilters = this.setFiltersFromQueryParams();
            this.setState({ filters: newFilters });
            this.handleFetchProducts(newFilters);
        }

        if (this._grid && prevProps.shopViewSettings !== this.props.shopViewSettings) {
            this._grid.recomputeGridSize();
        }
    }

    noContentRenderer = () => {
        return <div style={this.muiStyles.noResults as any}>{formatMessage("No results found")}</div>;
    };

    /**
     * Returns a ProductShopCart component for each product on the shopping search
     * If there's no data on the shopping cart then it shows an empty node.
     * @return React.Component
     */
    handleCellRender = ({ columnIndex, rowIndex, key, style }) => {
        const { collectionEditable, inAssortmentProducts, brandEntities, rawItemDisplayProperty, shopViewSettings } =
            this.props;
        const { grid, ProductCard } = this.state;
        // Loading, show tombstones
        if (!grid[rowIndex]) {
            return (
                <div style={style} key={key}>
                    <LoadingShimmer />
                </div>
            );
        }
        const node = grid[rowIndex][columnIndex];

        if (!node) {
            // dont render things to the right of the label
            return null;
        }

        if (!node.item) {
            return (
                <div style={style} key={key}>
                    <LoadingShimmer />
                </div>
            );
        }

        style = {
            ...style,
            height: node.height,
            width: node.width,
        };

        const product = node.item;

        const productId = product.getIn(ProductDisplayFieldPaths.FIELDS.PRODUCT_ID);
        const brandId = product.getIn(ProductDisplayFieldPaths.FIELDS.BRAND_ID);
        const brand = brandEntities[brandId];

        const primaryImageUrl = getProductHeroImage(brand, product, 400);
        const secondaryImageUrl = getProductImages(brand, product, 400)?.[1];

        const handleItemClick = () => {
            this.handlePreviewItem(productId);
        };

        const handleAddItem = (event: Event) => {
            event.stopPropagation();
            this.handleAddItem({ _id: productId });
        };

        const selected = !!inAssortmentProducts[productId];
        let onSelectItem = null;
        if (collectionEditable) {
            onSelectItem = handleAddItem;
        }

        const productShopView: IShopViewFields =
            getProductShopViewFields(rawItemDisplayProperty, product, shopViewSettings) ||
            getProductShopViewFieldsFallback();
        const tertiaryAttributes = [productShopView.subTitle, productShopView.color];
        const variantColors = product.getIn(ProductDisplayFieldPaths.FIELDS.VARIANT_COLORS);
        const variantColorIds = getVariantColorIds(variantColors);
        const additionalColors = getCollectionSwatches(product);

        return (
            <div data-test={`shop-product-card-${node.index}`} style={style} key={node.index}>
                <ProductCard
                    isSortable={false}
                    id={productId}
                    productId={productId}
                    imageUrl={primaryImageUrl}
                    secondaryImageUrl={secondaryImageUrl}
                    brandName={productShopView.brandName}
                    primaryAttribute={productShopView.title}
                    secondaryAttribute={productShopView.retailPrice}
                    tertiaryAttributes={tertiaryAttributes}
                    costPrice={productShopView.costPrice}
                    additionalColors={additionalColors}
                    onSelectItem={onSelectItem}
                    selected={selected}
                    showLinking={false}
                    onLinkItem={this.handleLinkItem}
                    index={node.index}
                    onItemClick={handleItemClick}
                    hasAdditionalVariants={variantColorIds.length > 1 && additionalColors.length <= 1}
                    allowDuplicateProduct={this.props.allowDuplicateProduct}
                    shopViewSettings={shopViewSettings}
                />
            </div>
        );
    };

    handleLinkItem = () => {
        // no-op
    };

    handleAddItem = (product) => {
        const { itemsId, items, onAddItem, router } = this.props;
        const productId = product._id;

        return onAddItem({
            itemsId,
            attributes: {
                fields: {
                    id: { string_value: uuid() },
                    product_id: { string_value: productId },
                },
            },
            index: items ? items.size : 0,
            productId,
            collectionId: router.params.id,
        });
    };

    rowHeight = ({ index }) => {
        const { ProductCard } = this.state;
        return ProductCard.height + ProductCard.verticalOffset;
    };

    handleIsRowLoaded = ({ index }) => {
        const { globalProducts } = this.props;
        const product = globalProducts.get(index);
        return !!product;
    };

    handleLoadMoreRows = ({ startIndex, stopIndex }) => {
        const { onFetchNextGlobalProducts, globalProductsIsFetching, globalProductNextPageQuery } = this.props;
        if (globalProductNextPageQuery && !globalProductsIsFetching) {
            onFetchNextGlobalProducts({ modal: false, merchantId: this.props.merchantId });
        }
    };

    handleSectionRendered = ({ columnStartIndex, columnStopIndex, rowStartIndex, rowStopIndex }) => {
        const { columns } = this.state;
        const startIndex = rowStartIndex * columns + columnStartIndex;
        const stopIndex = rowStopIndex * columns + columnStopIndex;

        this._onRowsRendered({
            startIndex,
            stopIndex,
        });
    };

    handleSetGridRef = (ref) => (this._grid = ref);

    render() {
        const {
            overrideStyles = {
                gridWidth: undefined,
                viewHorizontalOffset: undefined,
                viewWidth: undefined,
                headerHeight: 0,
            },
        } = this.props;
        const { columns, estimatedRows, ProductCard } = this.state;
        const { gridWidth, viewHorizontalOffset, viewWidth, headerHeight } = overrideStyles;
        const newViewWidth = viewWidth !== undefined ? viewWidth : ProductCard.width;
        const newViewHorizontalOffset =
            viewHorizontalOffset !== undefined ? viewHorizontalOffset : ProductCard.horizontalOffset;

        return (
            <AutoSizer>
                {({ width: outsideWidth, height: outsideHeight }) => (
                    <div {...this.getStyles(outsideWidth, outsideHeight)}>
                        <div className="entriesContainer">
                            <div ref={this.bindRef} className="results">
                                <InfiniteLoader
                                    isRowLoaded={this.handleIsRowLoaded}
                                    loadMoreRows={this.handleLoadMoreRows as any}
                                    rowCount={10000}
                                    threshold={1}
                                >
                                    {({ onRowsRendered }) => {
                                        this._onRowsRendered = onRowsRendered;
                                        return (
                                            <AutoSizer>
                                                {({ height, width }) => {
                                                    const newGridWidth = gridWidth !== undefined ? gridWidth : width;
                                                    return (
                                                        <Grid
                                                            cellRenderer={this.handleCellRender}
                                                            columnCount={columns}
                                                            columnWidth={
                                                                estimatedRows
                                                                    ? newViewWidth + newViewHorizontalOffset
                                                                    : newGridWidth
                                                            }
                                                            height={height - headerHeight}
                                                            rowCount={estimatedRows}
                                                            noContentRenderer={this.noContentRenderer}
                                                            rowHeight={this.rowHeight}
                                                            width={width}
                                                            ref={this.handleSetGridRef}
                                                            onSectionRendered={this.handleSectionRendered}
                                                            overscanRowCount={2}
                                                        />
                                                    );
                                                }}
                                            </AutoSizer>
                                        );
                                    }}
                                </InfiniteLoader>
                            </div>
                        </div>
                    </div>
                )}
            </AutoSizer>
        );
    }

    private setFiltersFromQueryParams = () => {
        const { location } = this.props;

        let filters = Map();
        try {
            filters = fromJS(JSON.parse(location.query.filters || "{}"));
        } catch (e) {
            // no-op
        }

        return filters;
    };

    private transformFiltersForQuery = (filters: Map<any, any>) => {
        const filterObject = filters.toJS();
        for (const key in filterObject) {
            if (key !== "__search") {
                filterObject[key] = Array.isArray(filterObject[key]) ? filterObject[key] : [filterObject[key]];
            }
        }

        return filterObject;
    };

    private handleFetchProducts = (filters: Map<any, any>) => {
        const formattedFilters = this.transformFiltersForQuery(filters);
        const { params, itemDisplayProperty } = this.props;

        const queries = [];
        let search = {};

        const fields =
            itemDisplayProperty && itemDisplayProperty.map((schemaKey) => schemaKey.get("field_name")).toJS();
        for (const key in formattedFilters) {
            if (key !== "__search") {
                const querySnippet = {
                    in: {
                        field: key,
                        values: formattedFilters[key].map((value) => {
                            return { string_value: value };
                        }),
                    },
                };
                queries.push(querySnippet);
            } else {
                search = {
                    query: formattedFilters[key],
                    ...(fields ? { fields } : { all_fields: true }),
                };
            }
        }

        if (params.brand) {
            queries.push({
                in: {
                    field: "_brand_id",
                    values: [{ string_value: params.brand }],
                },
            });
        }

        this.props.onFetchGlobalProducts({ filter: queries, search, merchantId: this.props.merchantId });
    };

    private handlePreviewItem = (productId) => {
        const { router } = this.props;
        router.replace({
            pathname: router.location.pathname,
            query: {
                ...router.location.query,
                productId,
            },
        });
    };

    private bindRef = (ref) => {
        this.setState({
            scrollDiv: ref,
        });
    };

    private getStyles = (width, height) => {
        return merge(
            {
                padding: "0px",
                flex: "1",
                display: "flex",
                height,
                width,
            },
            $(" > header", {
                marginLeft: "0px",
            }),
            $(" .entriesContainer", {
                // backgroundColor: "#f5f5f5",
                width: "100%",
                height: "100%",
                display: "flex",
                flexDirection: "column",
            }),
            // $(" .ReactVirtualized__Grid", {
            //     backgroundColor: "#f5f5f5",
            //     // width: `${width}px !important`,
            // }),
            $(" .ReactVirtualized__Grid__innerScrollContainer", {
                margin: "0 auto",
            }),
            $(" .results", {
                flexShrink: 1,
                flexGrow: 1,
            }),
            $(" .node", {
                // margin: "10px !important",
                padding: "8px !important",
            }),
            $(" .separatorLabel", {
                fontSize: "18px !important",
                background: "transparent !important",
                borderBottom: "none !important",
                paddingLeft: "8px !important",
            }),
            $(" .deliveryDates", {
                fontSize: "24px",
                fontWeight: "bold",
                width: "50%",
                margin: "0 auto",
                textAlign: "center",
                // maxWidth: `${MAX_WIDTH}px`,
            })
        );
    };
}
